import { motion } from 'framer-motion'
import type { SkinCondition } from '@/types/survey'
import { SKIN_CONDITIONS_OPTIONS } from '@/types/survey'

interface SkinConditionsSelectorProps {
  selectedConditions: SkinCondition[]
  onToggle: (condition: SkinCondition) => void
}

export function SkinConditionsSelector({
  selectedConditions,
  onToggle,
}: SkinConditionsSelectorProps) {
  return (
    <div className="w-full max-w-md mx-auto">
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-2xl sm:text-3xl font-semibold text-gray-900 text-center mb-2"
      >
        Skin Conditions
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-gray-500 text-center mb-2"
      >
        Select any current skin concerns
      </motion.p>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="text-sm text-gray-400 text-center mb-6"
      >
        Select all that apply
      </motion.p>
      <div className="space-y-3">
        {SKIN_CONDITIONS_OPTIONS.map((option, index) => {
          const isSelected = selectedConditions.includes(option.value)
          const isNoneOption = option.value === 'none'

          return (
            <motion.button
              key={option.value}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              onClick={() => onToggle(option.value)}
              className={`
                w-full p-4 rounded-xl border-2 text-left transition-all
                ${
                  isSelected
                    ? 'border-[#46C0C6] bg-[#46C0C6]/5'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }
                ${isNoneOption ? 'mb-2' : ''}
              `}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`
                    w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0
                    ${isSelected ? 'border-[#46C0C6] bg-[#46C0C6]' : 'border-gray-300'}
                  `}
                >
                  {isSelected && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-900">{option.label}</span>
                  {option.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                  )}
                </div>
              </div>
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}
